/**
 * TourneyNation widget loader — the script organizers paste on their site.
 *
 * Snippet shape:
 *   <button data-tn-register
 *           data-key="tn_pub_..."
 *           data-event="EVENT_UUID"
 *           data-mode="popup">
 *     Register for Spring Classic
 *   </button>
 *   <script async src="https://widgets.tourneynation.com/v1/loader.js"></script>
 *
 * Or let TourneyNation draw the button (black pill, white circle with the
 * TN star, white label), the same on every organizer's site — for hybrid
 * pages that keep their own registration next to it:
 *   <div data-tn-button data-key="tn_pub_..." data-event="EVENT_UUID"
 *        data-product="register" data-size="compact" data-width="full"
 *        data-variant="white"></div>
 * data-variant="white" inverts it for black or dark backgrounds.
 * data-match="#their-button" sizes it to the organizer's own button (never
 * taller than theirs); --tn-button-height in their CSS does the same by hand.
 * It takes the same data-key / data-event / data-mode / data-product as a
 * trigger. Anything inside the div shows only if the script doesn't run.
 *
 * On script load, every existing data-tn-register element gets a
 * click handler attached. New elements added after script load are
 * picked up too via a MutationObserver.
 *
 * On click:
 *   - data-mode="redirect" OR mobile (≤640px viewport) →
 *     navigate to register.tourneynation.com/?via=KEY&event=EVENT.
 *   - data-mode="popup" (default) on desktop →
 *     open a centered modal overlay containing a sandboxed iframe
 *     pointing at widgets.tourneynation.com/?key=KEY&event=EVENT.
 *
 * The bundle is intentionally vanilla TS (no React, no framework).
 * It loads on third-party origins where we have no idea what the host
 * page already includes; we don't want to ship a duplicate React
 * runtime on every page that embeds.
 *
 * See docs/EMBED_WIDGET_FEATURE_DESIGN.md §3a, §8.
 */

const WIDGETS_ORIGIN_PROD = 'https://widgets.tourneynation.com';
const REGISTER_ORIGIN_PROD = 'https://register.tourneynation.com';

/**
 * The environment this loader was served from. Loaded from
 * widgets.<env>.tourneynation.com (dev, staging…), it opens that same
 * environment, so a snippet copied from the dev admin works on dev without
 * page-level overrides; widgets.tourneynation.com is production. Only
 * TourneyNation widgets hosts count: a copy served from anywhere else keeps
 * the production default.
 */
function originsFromScriptSrc(src: string | null | undefined): { widgets: string; register: string } | null {
  if (!src) return null;
  let url: URL;
  try {
    url = new URL(src);
  } catch {
    return null;
  }
  if (url.protocol !== 'https:') return null;
  const m = /^widgets((?:\.[a-z0-9-]+)*)\.tourneynation\.com$/.exec(url.hostname);
  return m ? { widgets: url.origin, register: `https://register${m[1]}.tourneynation.com` } : null;
}

// document.currentScript is only set while this (classic, IIFE) script first
// runs, so read it here at the top of the module.
const SCRIPT_ORIGINS =
  typeof document !== 'undefined'
    ? originsFromScriptSrc((document.currentScript as HTMLScriptElement | null)?.src)
    : null;

// Order: an explicit override (set window.__TN_WIDGETS_ORIGIN__ /
// __TN_REGISTER_ORIGIN__ before the script loads, e.g. to point at
// localhost), then the environment the script came from, then production.
const WIDGETS_ORIGIN =
  (typeof window !== 'undefined' && (window as any).__TN_WIDGETS_ORIGIN__) ||
  SCRIPT_ORIGINS?.widgets ||
  WIDGETS_ORIGIN_PROD;
const REGISTER_ORIGIN =
  (typeof window !== 'undefined' && (window as any).__TN_REGISTER_ORIGIN__) ||
  SCRIPT_ORIGINS?.register ||
  REGISTER_ORIGIN_PROD;

const MOBILE_BREAKPOINT_PX = 640;
const MODAL_WIDTH_PX = 680;
const MODAL_MIN_HEIGHT_PX = 560;
// Cap so the modal doesn't get awkwardly tall (lots of empty space on short
// steps) on very large monitors; below this it scales with the viewport
// (calc(100vh - 40px)), so smaller screens still use the space they have.
const MODAL_MAX_HEIGHT_PX = 760;
const TRIGGER_SELECTOR = '[data-tn-register]';
// An element the loader renders the TourneyNation button into.
const BUTTON_SELECTOR = '[data-tn-button]';
// Opt-in trigger styling. When a trigger carries data-tn-style="theme" the
// loader paints it from the organizer's theme tokens (the same ones the
// Theme Manager / theme studio produce) so the on-site button matches the
// widget without the organizer touching their own site CSS — important on
// hosts like Wix where the button can't inherit page styles.
const TRIGGER_STYLE_ATTR = 'data-tn-style';

type DisplayMode = 'popup' | 'redirect';
// 'register'    → straight to the division selector
// 'sponsorship' → straight to the sponsor flow
// 'choose'      → the Register-vs-Sponsor chooser (only meaningful when the
//                 event has both products; the default when data-product is
//                 absent, preserving existing snippets' behavior)
// 'bulk'        → one team, one or more events, one payment. Starts from
//                 the event list, so data-event is ignored (a single-event
//                 button should use 'register').
type Product = 'register' | 'sponsorship' | 'choose' | 'bulk';

interface TriggerConfig {
  publicKey: string;
  eventId: string | null;
  mode: DisplayMode;
  // Which product this trigger opens. 'registration' (default) opens the
  // team/foursome flow; 'sponsorship' deep-links to the sponsor flow (the
  // "Sponsor this event" button). Same iframe/redirect target either way.
  product: Product;
}

let activeModal: HTMLElement | null = null;
let messageListenerInstalled = false;
// Public keys whose theme stylesheet we've already injected. Declared up
// here, above init(), on purpose: init() wires (and therefore styles) any
// trigger already in the DOM synchronously, so a declaration further down
// the module is still in its temporal dead zone at that point and the very
// first themed trigger on the page throws.
const themedKeys = new Set<string>();

// Inject preconnect + dns-prefetch hints for our widget/register origins so
// the browser has DNS + TLS ready before the click. Idempotent.
function preconnect() {
  const head = document.head || document.documentElement;
  for (const origin of [WIDGETS_ORIGIN, REGISTER_ORIGIN]) {
    for (const rel of ['preconnect', 'dns-prefetch']) {
      if (document.querySelector(`link[rel="${rel}"][href="${origin}"]`)) continue;
      const link = document.createElement('link');
      link.rel = rel;
      link.href = origin;
      head.appendChild(link);
    }
  }
}

function wireExisting() {
  document.querySelectorAll<HTMLElement>(TRIGGER_SELECTOR).forEach(wireOne);
  document.querySelectorAll<HTMLElement>(BUTTON_SELECTOR).forEach(renderButton);
}

function wireOne(el: HTMLElement) {
  // Idempotent — bail if already wired.
  if ((el as any).__tnWired) return;
  (el as any).__tnWired = true;

  maybeStyleTrigger(el);

  el.addEventListener('click', (e) => {
    const cfg = readConfig(el);
    if (!cfg) {
      // Misconfigured trigger (no data-key). Don't intercept the click —
      // let any default behavior run. Log a hint for the developer.
      // eslint-disable-next-line no-console
      console.warn('[tn-loader] data-tn-register trigger missing data-key', el);
      return;
    }
    e.preventDefault();
    handleTrigger(cfg);
  });
}

// ── the TourneyNation button ─────────────────────────────────────────────

// The TN star mark (frontend/tourneynation/public/icons/tn-star.svg).
const TN_STAR_SVG =
  '<svg viewBox="829 323 263 309" aria-hidden="true" focusable="false">' +
  '<polygon points="960.43,323.93 922.25,441.45 998.61,441.45"/>' +
  '<polygon points="957.14,463.67 984.09,534.53 1003.22,475.17 1026.55,475.17 992.13,581.99 1060.39,631.59 1022.21,514.07 1091.61,463.65 957.15,463.65"/>' +
  '<polygon points="916.05,591.2 960.43,558.96 973.14,568.19 946.25,497.49"/>' +
  '<polygon points="829.25,463.65 898.65,514.07 860.47,631.59 885.59,613.33 933.82,463.65"/>' +
  '</svg>';

// Drawn in a shadow root so the host page's CSS can't restyle it, and its
// own styles can't leak out. Black pill, white circle inset on the left
// with the black star, white label (the sports app's notification bar).
const TN_BUTTON_CSS = `
  :host { display: inline-block; vertical-align: middle; }
  :host([data-width="full"]) { display: block; }
  /* Everything scales from one height, --h: 52px by default (44px compact),
     or the page's --tn-button-height (set from their CSS, or by data-match
     to their own button's height), kept between 40px and 64px. At 52px:
     16px label, 12px gap, 22px right padding; at 44px: 15px, 10px, 18px. */
  button {
    --h: clamp(40px, var(--tn-button-height, 52px), 64px);
    all: unset;
    box-sizing: border-box;
    display: inline-flex;
    align-items: center;
    gap: calc(var(--h) * 0.25 - 1px);
    width: 100%;
    height: var(--h);
    padding: 0 calc(var(--h) * 0.5 - 4px) 0 6px;
    border-radius: 999px;
    background: #0B0F14;
    color: #FFFFFF;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
    font-size: calc(var(--h) * 0.125 + 9.5px);
    font-weight: 600;
    line-height: 1;
    letter-spacing: -0.01em;
    white-space: nowrap;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
    transition: background 0.15s ease, transform 0.1s ease;
  }
  :host([data-size="compact"]) button { --h: clamp(40px, var(--tn-button-height, 44px), 64px); }
  :host([data-width="full"]) button, :host([data-tn-fill]) button { justify-content: center; }
  button:hover { background: #1F252D; }
  button:active { transform: scale(0.98); }
  button:focus-visible { outline: 2px solid #0B0F14; outline-offset: 3px; }
  .mark {
    flex-shrink: 0;
    width: calc(var(--h) - 12px);
    height: calc(var(--h) - 12px);
    border-radius: 50%;
    background: #FFFFFF;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  :host([data-width="full"]) .mark, :host([data-tn-fill]) .mark { position: absolute; left: 6px; }
  :host([data-width="full"]) button, :host([data-tn-fill]) button { position: relative; padding-left: calc(var(--h) + 6px); }
  .mark svg { width: 58%; height: 58%; margin-top: -1px; fill: #0B0F14; }
  /* Inverted, for black or dark backgrounds: white pill, black circle with
     the white star, black label. */
  :host([data-variant="white"]) button { background: #FFFFFF; color: #0B0F14; }
  :host([data-variant="white"]) button:hover { background: #E9EBEE; }
  :host([data-variant="white"]) button:focus-visible { outline-color: #FFFFFF; }
  :host([data-variant="white"]) .mark { background: #0B0F14; }
  :host([data-variant="white"]) .mark svg { fill: #FFFFFF; }
`;

function buttonLabel(product: Product): string {
  return product === 'sponsorship' ? 'Sponsor with TourneyNation' : 'Register with TourneyNation';
}

/** Render the TourneyNation button into a data-tn-button element. Same
 *  config and click behavior as a data-tn-register trigger. */
function renderButton(host: HTMLElement) {
  if ((host as any).__tnWired) return;
  const cfg = readConfig(host);
  if (!cfg) {
    // eslint-disable-next-line no-console
    console.warn('[tn-loader] data-tn-button missing data-key', host);
    return;
  }
  if (typeof host.attachShadow !== 'function') return; // leave the fallback content
  (host as any).__tnWired = true;

  const root = host.shadowRoot ?? host.attachShadow({ mode: 'open' });
  const style = document.createElement('style');
  style.textContent = TN_BUTTON_CSS;
  const btn = document.createElement('button');
  btn.type = 'button';
  const mark = document.createElement('span');
  mark.className = 'mark';
  mark.innerHTML = TN_STAR_SVG;
  const label = document.createElement('span');
  label.textContent = buttonLabel(cfg.product);
  // The full label stays the accessible name even when matchSize shortens it.
  btn.setAttribute('aria-label', buttonLabel(cfg.product));
  btn.append(mark, label);
  btn.addEventListener('click', (e) => {
    e.preventDefault();
    // Re-read on click so attribute changes after render still apply.
    const current = readConfig(host);
    if (current) handleTrigger(current);
  });
  root.replaceChildren(style, btn);
  if (host.hasAttribute('data-match')) matchSize(host, label, buttonLabel(cfg.product));
}

/**
 * data-match="<selector>": size the button to the organizer's own button
 * so it's never much bigger than theirs. Takes their height (the CSS keeps
 * it between 40px and 64px) and at least their width. When the full label
 * would make it more than a quarter wider than theirs, the label shortens
 * to "TourneyNation" (the star still beside it; the accessible name stays
 * the full label). Follows their button as it resizes.
 */
function matchSize(host: HTMLElement, label: HTMLElement, fullLabel: string, attempt = 0) {
  const selector = host.getAttribute('data-match') || '';
  let target: Element | null = null;
  try {
    target = document.querySelector(selector);
  } catch {
    // eslint-disable-next-line no-console
    console.warn('[tn-loader] data-match is not a valid selector', selector);
    return;
  }
  if (!target) {
    // Their button may mount after ours (SPAs): look again for ~3s.
    if (attempt < 30) window.setTimeout(() => matchSize(host, label, fullLabel, attempt + 1), 100);
    else console.warn('[tn-loader] data-match found nothing for', selector); // eslint-disable-line no-console
    return;
  }
  const apply = () => {
    const box = target!.getBoundingClientRect();
    if (box.height <= 0) {
      // Hidden (e.g. their button only shows on desktop): back to defaults.
      host.style.removeProperty('--tn-button-height');
      host.style.removeProperty('min-width');
      host.removeAttribute('data-tn-fill');
      return;
    }
    host.style.setProperty('--tn-button-height', `${Math.round(box.height)}px`);
    if (host.getAttribute('data-width') === 'full') return;
    // Measure the full label at this height, then decide.
    host.style.removeProperty('min-width');
    host.removeAttribute('data-tn-fill');
    label.textContent = fullLabel;
    if (host.getBoundingClientRect().width > box.width * 1.25) label.textContent = 'TourneyNation';
    host.style.minWidth = `${Math.round(box.width)}px`;
    host.setAttribute('data-tn-fill', '');
  };
  apply();
  if (typeof ResizeObserver === 'function') new ResizeObserver(apply).observe(target);
  else window.addEventListener('resize', apply);
}

// ── trigger theming ──────────────────────────────────────────────────────

/**
 * Paint a trigger from the organizer's theme when it opts in with
 * data-tn-style="theme". Two steps:
 *   1. Inject the org's rendered token overrides (/themes/by-key/{key}.css)
 *      onto the host page as a <link>, so the --tn-* custom properties
 *      resolve on this document. The endpoint is same-origin as the widget,
 *      always 200, and edge-cached; a missing/blank theme just leaves the
 *      defaults, which the fallbacks below cover.
 *   2. Add the .tn-trigger class, whose rules consume --tn-embed-btn-* with
 *      the SAME default chain the widget's theme-tokens.css defines (embed
 *      button → event card → hardcoded), since the host page never loads
 *      that stylesheet — only the org's overrides.
 */
function maybeStyleTrigger(el: HTMLElement) {
  const style = (el.getAttribute(TRIGGER_STYLE_ATTR) || '').toLowerCase();
  if (style !== 'theme') return;
  const key = el.getAttribute('data-key');
  if (key) ensureThemeTokens(key);
  ensureTriggerStyles();
  el.classList.add('tn-trigger');
}

function ensureThemeTokens(key: string) {
  if (themedKeys.has(key)) return;
  themedKeys.add(key);
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = `${WIDGETS_ORIGIN}/themes/by-key/${encodeURIComponent(key)}.css`;
  link.setAttribute('data-tn-theme', key);
  (document.head || document.documentElement).appendChild(link);
}

function ensureTriggerStyles() {
  if (document.getElementById('tn-trigger-styles')) return;
  const style = document.createElement('style');
  style.id = 'tn-trigger-styles';
  // Fallback chain mirrors frontend/registration/src/styles/theme-tokens.css:
  // embed-button token → event-card token → hardcoded default. The host page
  // only ever carries the org's *overrides*, so these inline fallbacks are
  // what render for any token the org left at default.
  style.textContent = `
    .tn-trigger {
      display: inline-block;
      padding: 14px 26px;
      font-family: var(--tn-font-body, system-ui, -apple-system, 'Segoe UI', sans-serif);
      font-size: var(--tn-text-card-title-size, 16px);
      font-weight: var(--tn-text-card-title-weight, 600);
      line-height: 1;
      color: var(--tn-embed-btn-fg, var(--tn-event-card-fg, #1a1a2e));
      background: var(--tn-embed-btn-bg, var(--tn-event-card-bg, #ffffff));
      border: 1.5px solid var(--tn-embed-btn-border, var(--tn-event-card-border, #e5e7eb));
      border-radius: var(--tn-embed-btn-radius, var(--tn-radius-lg, 16px));
      cursor: pointer;
      /* Triggers are often <a> on real sites — drop the inherited underline. */
      text-decoration: none;
      -webkit-appearance: none;
      appearance: none;
      transition: background 0.18s ease, color 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease;
    }
    .tn-trigger:hover {
      background: var(--tn-embed-btn-bg-hover, var(--tn-event-card-bg-hover, var(--tn-embed-btn-bg, var(--tn-event-card-bg, #ffffff))));
      color: var(--tn-embed-btn-fg-hover, var(--tn-event-card-fg-hover, var(--tn-embed-btn-fg, var(--tn-event-card-fg, #1a1a2e))));
      border-color: var(--tn-embed-btn-border-hover, var(--tn-event-card-border-hover, #7c00ff));
      box-shadow: 0 4px 14px rgba(0, 0, 0, 0.10);
    }
    .tn-trigger:focus-visible {
      outline: 2px solid var(--tn-embed-btn-border-hover, var(--tn-event-card-border-hover, #7c00ff));
      outline-offset: 2px;
    }
  `;
  (document.head || document.documentElement).appendChild(style);
}

function readConfig(el: HTMLElement): TriggerConfig | null {
  const publicKey = el.getAttribute('data-key');
  if (!publicKey) return null;
  const eventId = el.getAttribute('data-event');
  const modeAttr = (el.getAttribute('data-mode') || 'popup').toLowerCase();
  const mode: DisplayMode = modeAttr === 'redirect' ? 'redirect' : 'popup';
  const productAttr = (el.getAttribute('data-product') || '').toLowerCase();
  const product: Product =
    productAttr === 'sponsorship' ? 'sponsorship'
    : (productAttr === 'register' || productAttr === 'registration') ? 'register'
    : productAttr === 'bulk' ? 'bulk'
    : 'choose';
  return { publicKey, eventId, mode, product };
}

/**
 * Build the widget URL (path + product) shared by popup and redirect
 * modes. The caller adds the mode-specific key param (`key` for popup,
 * `via` for redirect).
 *
 *   sponsorship + event → /events/{id}/sponsor
 *   register + event     → /events/{id}/register   (straight to divisions)
 *   choose + event       → /events/{id}            (the chooser fork)
 *   no event, {product}  → /?product={product}      (LandingPage routes it)
 *   bulk                 → /?product=bulk (data-event ignored)
 */
function buildFlowUrl(origin: string, cfg: TriggerConfig): URL {
  const url = new URL('/', origin);
  if (cfg.product === 'bulk') {
    url.searchParams.set('product', 'bulk');
  } else if (cfg.eventId) {
    const base = `/events/${encodeURIComponent(cfg.eventId)}`;
    url.pathname =
      cfg.product === 'sponsorship' ? `${base}/sponsor`
      : cfg.product === 'register' ? `${base}/register`
      : base; // 'choose' → the fork
  } else if (cfg.product === 'sponsorship') {
    url.searchParams.set('product', 'sponsorship');
  } else if (cfg.product === 'register') {
    url.searchParams.set('product', 'register');
  }
  return url;
}

function handleTrigger(cfg: TriggerConfig) {
  // Mobile auto-fallback: popup modals on small viewports are bad UX
  // (they're cramped, lose focus on keyboard open, fight the host
  // site's mobile layout). Always navigate on small screens.
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= MOBILE_BREAKPOINT_PX;
  if (cfg.mode === 'redirect' || isMobile) {
    window.location.href = redirectUrl(cfg);
    return;
  }
  openModal(cfg);
}

function redirectUrl(cfg: TriggerConfig): string {
  const url = buildFlowUrl(REGISTER_ORIGIN, cfg);
  url.searchParams.set('via', cfg.publicKey);
  return url.toString();
}

// ── modal ───────────────────────────────────────────────────────────────

function openModal(cfg: TriggerConfig) {
  if (activeModal) return; // single-instance — ignore duplicate clicks

  installMessageListener();

  const backdrop = document.createElement('div');
  backdrop.setAttribute('role', 'dialog');
  backdrop.setAttribute('aria-modal', 'true');
  backdrop.setAttribute('aria-label', 'TourneyNation registration');
  backdrop.style.cssText = [
    'position:fixed', 'inset:0',
    'background:rgba(15,23,42,0.55)',
    'z-index:2147483646', // one below the modal container
    'display:flex', 'align-items:center', 'justify-content:center',
    'padding:24px',
    'animation:tnFadeIn 0.18s ease',
  ].join(';');
  // Simple keyframes — inline so we don't pollute the host's
  // stylesheet. Idempotent: if we've inserted these before, skip.
  ensureKeyframes();

  const modal = document.createElement('div');
  modal.style.cssText = [
    'position:relative',
    `width:100%`, `max-width:${MODAL_WIDTH_PX}px`,
    // Grow with the viewport (up to a comfortable cap) instead of sitting at
    // a fixed short height — taller screens get a longer modal, so most flows
    // fit without an internal scrollbar. Always leave a 40px breathing gap
    // top+bottom so the card floats on the backdrop.
    `height:min(${MODAL_MAX_HEIGHT_PX}px, calc(100vh - 40px))`,
    `min-height:min(${MODAL_MIN_HEIGHT_PX}px, calc(100vh - 40px))`,
    'background:#ffffff', 'border-radius:16px',
    'box-shadow:0 24px 64px rgba(0,0,0,0.25)',
    'overflow:hidden',
    'display:flex', 'flex-direction:column',
    'animation:tnSlideIn 0.22s cubic-bezier(0.34,1.56,0.64,1)',
  ].join(';');

  // Close button. Visible top-right, large hit area. It lives in a shadow
  // root: site themes style every <button> (an Elementor kit turned it into
  // a solid orange square with the × lost), and the host page's CSS can't
  // reach inside. Only the positioned holder is in the page, pinned with
  // !important. A solid fill + shadow makes it read as floating above the
  // flow, including over the iframe's own scrollbar on a long screen.
  const closeHost = document.createElement('div');
  closeHost.style.cssText = [
    'position:absolute', 'top:12px', 'right:12px', 'z-index:2',
    'width:34px', 'height:34px', 'margin:0', 'padding:0', 'border:0', 'background:none',
  ].map((d) => `${d} !important`).join(';');
  const closeRoot = closeHost.attachShadow({ mode: 'open' });
  closeRoot.innerHTML = `<style>${CLOSE_BUTTON_CSS}</style>`;
  const closeBtn = document.createElement('button');
  closeBtn.type = 'button';
  closeBtn.className = 'tn-embed-close';
  closeBtn.setAttribute('aria-label', 'Close registration');
  closeBtn.innerHTML =
    '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><path d="M3 3l10 10M13 3L3 13"/></svg>';
  closeBtn.addEventListener('click', closeModal);
  closeRoot.appendChild(closeBtn);

  // Sandboxed iframe.
  // - allow-scripts: needed for the React app to run
  // - allow-forms: form submission inside the registration flow
  // - allow-popups: Stripe 3DS opens a popup for some cards
  // - allow-same-origin: needed for sessionStorage and our own /embed/v1/* requests
  // - allow-storage-access-by-user-activation: lets coaches optionally
  //   request first-party storage on supported browsers (Phase 2 "remember me")
  const iframe = document.createElement('iframe');
  // The popup-mode iframe content is deployed to the WIDGETS_ORIGIN
  // bucket root (the embed-mode build's embed.html is uploaded as
  // index.html). loader.js itself lives at /v1/loader.js and the embed
  // runtime API at /embed/v1/* — no path collision.
  //
  // When data-event is set, point the iframe straight at the event's
  // registration page (/events/{id}) so the coach lands in that
  // event's flow — the most common organizer scenario, a Register
  // button on a specific event page. Without data-event, the iframe
  // opens at the root landing page (event picker). This mirrors the
  // redirect-mode URL shape exactly, so both modes route through the
  // same /events/:eventId React route.
  const src = buildFlowUrl(WIDGETS_ORIGIN, cfg);
  src.searchParams.set('key', cfg.publicKey);
  iframe.src = src.toString();
  iframe.title = 'TourneyNation registration';
  iframe.setAttribute(
    'sandbox',
    'allow-scripts allow-forms allow-popups allow-same-origin allow-storage-access-by-user-activation',
  );
  // Let the done screen's Copy link write to the clipboard (browsers block
  // it in an iframe unless the embedding page grants it).
  iframe.setAttribute('allow', 'clipboard-write');
  iframe.style.cssText = [
    'flex:1', 'width:100%', 'border:0', 'background:transparent',
  ].join(';');

  modal.appendChild(closeHost);
  modal.appendChild(iframe);
  backdrop.appendChild(modal);

  // Click-outside-to-close. Don't catch clicks inside the modal box.
  backdrop.addEventListener('click', (e) => {
    if (e.target === backdrop) closeModal();
  });

  document.body.appendChild(backdrop);
  // Lock background scroll while the modal is open.
  const previousOverflow = document.body.style.overflow;
  document.body.style.overflow = 'hidden';
  (backdrop as any).__tnPreviousOverflow = previousOverflow;

  // ESC closes.
  document.addEventListener('keydown', escListener);

  activeModal = backdrop;
}

function closeModal() {
  if (!activeModal) return;
  const previousOverflow = (activeModal as any).__tnPreviousOverflow ?? '';
  document.body.style.overflow = previousOverflow;
  activeModal.remove();
  document.removeEventListener('keydown', escListener);
  activeModal = null;
}

/** The popup's close button, inside its shadow root (see openModal). */
const CLOSE_BUTTON_CSS = `
  :host { all: initial; }
  .tn-embed-close {
    all: initial;
    box-sizing: border-box;
    width: 34px; height: 34px;
    border: 0; border-radius: 50%;
    background: #ffffff; color: #1a1a2e;
    box-shadow: 0 2px 10px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.04);
    cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    padding: 0;
    transition: background 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease;
  }
  .tn-embed-close:hover { background: #f1f2f4; transform: scale(1.06); box-shadow: 0 3px 14px rgba(0,0,0,0.22), 0 0 0 1px rgba(0,0,0,0.06); }
  .tn-embed-close:active { transform: scale(0.96); }
  .tn-embed-close:focus-visible { outline: 2px solid #2563eb; outline-offset: 2px; }
  svg { display: block; }
`;

function escListener(e: KeyboardEvent) {
  if (e.key === 'Escape') closeModal();
}

function ensureKeyframes() {
  if (document.getElementById('tn-loader-keyframes')) return;
  const style = document.createElement('style');
  style.id = 'tn-loader-keyframes';
  style.textContent = `
    @keyframes tnFadeIn  { from { opacity: 0 } to { opacity: 1 } }
    @keyframes tnSlideIn { from { transform: translateY(12px); opacity: 0 } to { transform: translateY(0); opacity: 1 } }
  `;
  document.head.appendChild(style);
}

// ── postMessage bus ─────────────────────────────────────────────────────

function installMessageListener() {
  if (messageListenerInstalled) return;
  messageListenerInstalled = true;

  window.addEventListener('message', (event) => {
    // Only accept messages from our widgets origin. Defends against
    // a third-party iframe trying to fire close events on our modal.
    if (event.origin !== WIDGETS_ORIGIN && !isLocalhostOrigin(event.origin)) return;
    if (!event.data || typeof event.data !== 'object') return;

    switch (event.data.type) {
      case 'tn:registration:complete':
      case 'tn:sponsorship:complete':
        // Re-emit on host page as a CustomEvent so organizer JS can
        // listen for it (e.g. fire their own analytics, redirect to
        // a thank-you page, etc.). Detail mirrors the iframe payload.
        // Re-emit under the same event type so listeners for either
        // product get exactly the event they registered for.
        window.dispatchEvent(
          new CustomEvent(event.data.type, { detail: event.data }),
        );
        // Auto-close the modal a beat after success so the user sees
        // the summary screen for ~2.5s before the modal dismisses.
        // The summary screen renders inside the iframe; closing the
        // modal doesn't lose any state because the receipt/confirmation
        // email already went out. A done screen with things left to do
        // (the parent link, the dashboard) sends autoClose: false and
        // stays until the visitor closes it.
        if (event.data.autoClose !== false) setTimeout(closeModal, 2500);
        break;

      case 'tn:registration:close':
        closeModal();
        break;
    }
  });
}

function isLocalhostOrigin(origin: string): boolean {
  return /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/.test(origin);
}

// ── public bootstrap ────────────────────────────────────────────────────
// Last in the module on purpose: when the script loads after the DOM is
// ready, init() renders data-tn-button elements synchronously, and that
// reads TN_BUTTON_CSS / TN_STAR_SVG — consts that must already be
// initialized.
(function init() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  // Warm the connection to our origins the moment the loader parses, so the
  // DNS lookup + TLS handshake are already done by the time the visitor
  // clicks — shaving a few hundred ms off the first widget open.
  preconnect();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', wireExisting, { once: true });
  } else {
    wireExisting();
  }

  // Catch elements added after script load (e.g. SPAs that mount
  // nav components asynchronously). Each new element with
  // data-tn-register gets a click handler attached automatically.
  const observer = new MutationObserver((records) => {
    for (const r of records) {
      r.addedNodes.forEach((node) => {
        if (!(node instanceof HTMLElement)) return;
        if (node.matches?.(TRIGGER_SELECTOR)) wireOne(node);
        node.querySelectorAll?.(TRIGGER_SELECTOR).forEach((el) =>
          wireOne(el as HTMLElement),
        );
        if (node.matches?.(BUTTON_SELECTOR)) renderButton(node);
        node.querySelectorAll?.(BUTTON_SELECTOR).forEach((el) =>
          renderButton(el as HTMLElement),
        );
      });
    }
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
})();
